import React, { useMemo, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHidden, RCCHookToast, RCCIcon, RCCServicesApi, RCCTooltip } from "react-common-components";
import Photo from "src/components/Photo";
import Title from "src/components/Title";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import formatNumber from "src/utils/formatNumber";
import calculatePercentOKR from "../../utils/calculatePercentOKR";
import { ICheckIn, IComponentCheckIn } from "./interface";
import ContainerValues, { ColumnFlex, ContainerUser, SpanComment } from "./styles";

const CheckIn: React.FC<IComponentCheckIn> = ({
	data,
	KRgoal,
	KRinitialValue,
	clearCheckInsOnModal,
	disabled
}) => {
	const { addToast } = RCCHookToast();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;
	const {confirm} = useConfirmModal();
	const {translate, currentLanguage} = useTranslation();
	const [checkIns, setCheckIns] = useState<ICheckIn[]>(data);
	

	const submitDelete = (id: number) => {	
		confirm(
			translate("Exclusão de Check In"), 
			`${translate("Deseja realmente excluir o Check In")}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "KR/DeleteCheckIn",
					urlParameters: {
						id
					}
				});
				addToast({
					color: "green",
					description: translate("Check In excluído com sucesso"),
					title: translate("Sucesso")
				});
	
				setCheckIns((prevState) => prevState.filter((okb004) => okb004.okb004Id !== id));
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir Check In"), addToast);
			},
			true
		);
	};
	
	const ListCheckIns = useMemo(() => {
		let percentAcumulated = 0;
		let valueAcumulated = 0;
		if(!checkIns || checkIns.length === 0){
			clearCheckInsOnModal();
			return <RCCAlert 
				description={translate("Não há nenhum Check In realizado")}
			/>;
		}

		const lastIndexOfCheckInList = checkIns.length - 1;

		return checkIns.map((okb004, idx) => {
			const percent = calculatePercentOKR(KRgoal, KRinitialValue, okb004.okb004Valor);
			percentAcumulated += percent;
			valueAcumulated += okb004.okb004Valor;
			return (
				<RCCGrid
					key={okb004.okb004Id}
					container
					item
					direction="column"
				>
					<div style={{display: "flex", alignItems: "center", position: "relative"}}>
						<Photo 
							hover={false}
							name={okb004.okb004Efa005Nome}
							link={okb004.okb004Efa005Imagem && `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${okb004.okb004Efa005Imagem}`}
							width={40}
							height={40}
						/>
						<ColumnFlex>
							<ContainerUser>
								<Title color="purple" size="md" styleInline="margin: 2px 5px 2px 5px;">
									{okb004.okb004Efa005Nome}
								</Title>
								<Title color="grey" size="xs" styleInline="margin: 2px 5px 2px 5px;">
									{formatDate(okb004.okb004Data, currentLanguage)}
								</Title>
							</ContainerUser>

							<ContainerValues>
								<Title color="grey" size="xs" styleInline="margin: 2px 5px 2px 5px;">
									{translate("Valor do Check In")} <RCCIcon name="checkSquare" style={{margin: "0 4px 0 2px"}}/> {formatNumber(okb004.okb004Valor, currentLanguage)}
								</Title>		
								<Title color="grey" size="xs" styleInline="margin: 2px 5px 2px 5px;">
									{translate("Percentual realizado")} <RCCIcon name="percent"  style={{margin: "0 4px 0 2px"}}/> {formatNumber(percent, currentLanguage)}
								</Title>		
							</ContainerValues>

							<RCCHidden only="xs">
								<ContainerValues>
									<Title color="grey" size="xs" styleInline="margin: 2px 5px 2px 5px;">
										{translate("Valor acumulado")} <RCCIcon name="checkCircle"  style={{margin: "0 4px 0 2px"}}/>  {formatNumber(valueAcumulated, currentLanguage)}
									</Title>
									<Title color="grey" size="xs" styleInline="margin: 2px 5px 2px 5px;">
										{translate("Percentual acumulado")} <RCCIcon name="percent"  style={{margin: "0 4px 0 2px"}}/> {formatNumber(percentAcumulated, currentLanguage)}
									</Title>
								</ContainerValues>
							</RCCHidden>
						</ColumnFlex>
						<div style={{ display: "flex"}}>
							<RCCTooltip
								color="red"
								text={translate("Excluir Check In")}
							>
								<RCCButton
									color="red"
									icon={{
										name: "trash"
									}}
									layout="isRounded"
									size="md"
									onClick={() => submitDelete(okb004.okb004Id)}
									disabled={idx !== lastIndexOfCheckInList || disabled}
								/>
							</RCCTooltip>

						</div>
					</div>

					<SpanComment>
						{okb004.okb004Comentario}
					</SpanComment>
				</RCCGrid>
			);

		});
	}, [checkIns, disabled]);

	return(
		<>
			{ListCheckIns}
		</>
	);
};

export default CheckIn;
